import { TrendSparkline } from "../components/TrendSparkline"
import {
  ExecutiveInfoTooltip,
  ExecutiveTooltipTrigger,
  SummaryHeroMetricCard,
  SummaryHeroMetricGrid,
  SummaryHeroMetricHeader,
  SummaryHeroMetricPercent,
  SummaryHeroMetricPeriod,
  SummaryHeroMetricSubtext,
  SummaryHeroMetricTitle,
  SummaryHeroMetricTitleRow,
  SummaryHeroMetricTooltip,
  SummaryHeroMetricValue,
  SummaryInnerCard,
  SummaryInnerSubtitle,
  SummaryInnerTitle,
  SummaryTotalItem,
  SummaryTotalLabel,
  SummaryTotals,
  SummaryTotalSubtext,
  SummaryTotalTrendIcon,
  SummaryTotalTrendLabel,
  SummaryTotalValue,
  SummaryTrendInlineIcon,
} from "./styled"
import type { ExecutiveMetric, GrowthMetric } from "./types"

interface ReportSummaryProps {
  executiveMetrics: ExecutiveMetric[]
  growthMetrics: GrowthMetric[]
  isTitlePage?: boolean
  hideZeroValues?: boolean
}

const executiveTooltipText: Record<string, string> = {
  "Total Engagement Actions":
    "Total number of tracked customer engagement actions during the selected reporting period, including thermostat and mobile app interactions.",
  "Thermostat Engagement":
    "Total customer interactions completed directly from Nuve thermostat devices, such as logo clicks and QR code scans.",
  "Mobile App Engagement":
    "Total customer interactions completed from the Nuve mobile app, including website visits, support tab clicks, technician requests, calls, and text actions.",
  "Total Conversions":
    "Number of customer actions that moved from initial engagement to a confirmed next step, such as continuing to the contractor website or technician request flow.",
}

const getTrendArrow = (trend?: string) => {
  if (trend === "up") return 1
  if (trend === "down") return -1
  return 0
}

const getDisplayPercent = (value: number) => Math.abs(value)
const formatDisplayValue = (value: number, hideZeroValues?: boolean) =>
  hideZeroValues && value === 0 ? "-" : value.toLocaleString()
const formatDisplayPercent = (value: number, hideZeroValues?: boolean) =>
  hideZeroValues && value === 0 ? "-" : `${getDisplayPercent(value).toLocaleString()}%`

export const ReportSummary = ({
  executiveMetrics,
  growthMetrics,
  hideZeroValues = false,
  isTitlePage = false,
}: ReportSummaryProps) => (
  <SummaryInnerCard $isTitlePage={isTitlePage}>
    <SummaryInnerTitle $isTitlePage={isTitlePage}>Engagement summary</SummaryInnerTitle>
    <SummaryInnerSubtitle $isTitlePage={isTitlePage}>How customers interacted with you this month</SummaryInnerSubtitle>
    <SummaryHeroMetricGrid>
      {executiveMetrics?.map(metric => {
        const tooltipId = `engagement-summary-${metric.title.toLowerCase().replace(/\W+/g, "-")}`

        return (
          <SummaryHeroMetricCard key={metric.title}>
            <SummaryHeroMetricHeader>
              <SummaryHeroMetricTitleRow>
                <SummaryHeroMetricTitle>{metric.title}</SummaryHeroMetricTitle>
              </SummaryHeroMetricTitleRow>
              <SummaryHeroMetricTooltip>
                <ExecutiveTooltipTrigger data-tooltip-id={tooltipId} tabIndex={0} />
              </SummaryHeroMetricTooltip>
            </SummaryHeroMetricHeader>
            <SummaryHeroMetricValue>{formatDisplayValue(metric.value, hideZeroValues)}</SummaryHeroMetricValue>
            <SummaryHeroMetricSubtext>
              {metric.direction === "up" || metric.direction === "down" ? (
                <SummaryTrendInlineIcon $direction={getTrendArrow(metric.direction)}>
                  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path d="M8 13V3" />
                    <path d="M4.5 6.5L8 3l3.5 3.5" />
                  </svg>
                </SummaryTrendInlineIcon>
              ) : null}
              <SummaryHeroMetricPercent $direction={getTrendArrow(metric.direction)}>
                {formatDisplayPercent(metric.percent, hideZeroValues)}{" "}
                <SummaryHeroMetricPeriod>past month</SummaryHeroMetricPeriod>
              </SummaryHeroMetricPercent>
            </SummaryHeroMetricSubtext>
            <TrendSparkline values={metric.sparkline} direction={metric.direction} />
            <ExecutiveInfoTooltip id={tooltipId} place="top" clickable delayHide={150}>
              {executiveTooltipText[metric.title]}
            </ExecutiveInfoTooltip>
          </SummaryHeroMetricCard>
        )
      })}
    </SummaryHeroMetricGrid>
    <SummaryTotals>
      {growthMetrics?.map(metric => {
        const trendDirection = getTrendArrow(metric.direction)

        return (
          <SummaryTotalItem key={metric.title}>
            <SummaryTotalLabel>{metric.title}</SummaryTotalLabel>
            <SummaryTotalValue>
              {trendDirection !== 0 ? (
                <SummaryTotalTrendIcon $direction={trendDirection}>
                  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                    <path d="M8 13V3" />
                    <path d="M4.5 6.5L8 3l3.5 3.5" />
                  </svg>
                </SummaryTotalTrendIcon>
              ) : null}
              <span>{formatDisplayValue(metric.value ?? 0, hideZeroValues)}</span>
              <SummaryTotalTrendLabel>last month</SummaryTotalTrendLabel>
            </SummaryTotalValue>
            <SummaryTotalSubtext>of {formatDisplayValue(metric.total ?? 0, hideZeroValues)} total</SummaryTotalSubtext>
          </SummaryTotalItem>
        )
      })}
    </SummaryTotals>
  </SummaryInnerCard>
)
